import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { theme } from '../theme';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { clearOdcLogo, readOdcLogo, writeOdcLogo } from '../utils/odcLogoStorage';

type Business = {
  id: string;
  business_name: string;
  onboarding_step: number;
  onboarding_complete: boolean;
};

const BUSINESS_TYPES = [
  'Real Estate',
  'Restaurant',
  'Retail Shop',
  'Doctor/Clinic',
  'Coaching Center',
  'Beauty Salon',
  'Car Dealer',
  'Other',
];

const steps = ['Business', 'Brand', 'Social', 'Voice', 'Calendar', 'Go live', 'Report'];

type BusinessProfile = {
  businessName: string;
  businessType: string;
  location: string;
  targetAudience: string;
  monthlyBudget: string;
  primaryGoal: string;
  socialPlatforms: string[];
  teamSize: string;
  businessAge: string;
  description?: string;
};

const dummyWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d, i) => ({
  day: d,
  title: `AI post #${i + 1} · Reel + story`,
}));

function friendlyError(err: unknown): string {
  const ax = err as {
    response?: { data?: { error?: string }; status?: number };
    message?: string;
  };
  const msg = ax.response?.data?.error;
  if (typeof msg === 'string' && msg.trim()) return msg;
  const st = ax.response?.status;
  if (st === 404) return 'We could not find your business. Try refreshing this page.';
  if (st === 403) return 'You do not have permission to do that.';
  if (st === 500) return 'Our servers hit a snag. Please try again in a moment.';
  return 'Something went wrong. Check your internet connection and try again.';
}

export function OnboardingPage() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [business, setBusiness] = useState<Business | null>(null);
  const [step, setStep] = useState(1);
  const [reportGenOk, setReportGenOk] = useState(true);
  const postCompleteTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [f1, setF1] = useState({
    business_name: '',
    business_type: '',
    city: '',
    state: '',
    location: '',
    description: '',
  });
  const [f2, setF2] = useState({
    logo_url: '',
    primary_color: '#10B981',
    tagline: '',
    target_audience: '',
  });
  const [logoDrag, setLogoDrag] = useState(false);
  const [waNumber, setWaNumber] = useState('');

  useEffect(() => {
    void (async () => {
      try {
        const { data } = await api.get<{ business: Business | null }>('/business/me');
        if (data.business?.onboarding_complete) {
          nav('/dashboard', { replace: true });
          return;
        }
        if (data.business) {
          setBusiness(data.business);
          setStep(Math.max(1, Math.min(6, data.business.onboarding_step || 1)));
          setF1((s) => ({ ...s, business_name: data.business!.business_name }));
        }
      } catch (e) {
        setError(friendlyError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [nav]);

  useEffect(() => {
    return () => {
      if (postCompleteTimer.current) clearTimeout(postCompleteTimer.current);
    };
  }, []);

  useEffect(() => {
    if (!business?.id) return;
    const saved = readOdcLogo(business.id);
    if (!saved) return;
    setF2((s) => (s.logo_url ? s : { ...s, logo_url: saved }));
  }, [business?.id]);

  function onLogoFile(file: File) {
    const r = new FileReader();
    r.onload = () => setF2((s) => ({ ...s, logo_url: String(r.result || '') }));
    r.readAsDataURL(file);
  }

  async function persistCurrentStep(bid: string) {
    if (step === 2) {
      const logo = f2.logo_url.trim();
      if (logo.startsWith('data:image')) {
        writeOdcLogo(bid, f2.logo_url);
        await api.put(`/business/${bid}`, {
          logo_url: 'uploaded',
          primary_color: f2.primary_color,
          tagline: f2.tagline,
          target_audience: f2.target_audience,
        });
      } else {
        clearOdcLogo(bid);
        await api.put(`/business/${bid}`, {
          logo_url: '',
          primary_color: f2.primary_color,
          tagline: f2.tagline,
          target_audience: f2.target_audience,
        });
      }
    }
    if (step === 3) {
      await api.put(`/business/${bid}`, {
        description: `Social: FB/IG/YT/LI intents saved. WhatsApp: ${waNumber || 'skipped'}`,
      });
    }
    if (step === 4) {
      await api.put(`/business/${bid}`, {
        description: 'Voice clone: samples pending or skipped (demo).',
      });
    }
    if (step === 5) {
      await api.put(`/business/${bid}`, {
        description: '90-day AI calendar approved.',
      });
    }
  }

  async function onContinue() {
    if (step === 7) return;
    setError(null);
    setBusy(true);
    try {
      if (!business && step === 1) {
        if (!f1.business_name.trim()) {
          setError('Please enter your business name to continue.');
          return;
        }
        const { data } = await api.post<{ business: Business; created?: boolean }>('/business', {
          business_name: f1.business_name,
          business_type: f1.business_type,
          city: f1.city,
          state: f1.state,
          location: f1.location,
          description: f1.description,
        });
        setBusiness(data.business);
        const currentStep = Math.max(1, Math.min(6, data.business.onboarding_step || 1));
        if (data.created) {
          await api.post(`/business/${data.business.id}/onboarding`, { step: 2 });
          setStep(2);
        } else if (currentStep === 1) {
          await api.post(`/business/${data.business.id}/onboarding`, { step: 2 });
          setStep(2);
        } else {
          setStep(currentStep);
        }
        return;
      }

      if (!business) return;
      const bid = business.id;

      try {
        await persistCurrentStep(bid);
      } catch (e) {
        setError(friendlyError(e));
        return;
      }

      if (step < 6) {
        await api.post(`/business/${bid}/onboarding`, { step: step + 1 });
        setStep(step + 1);
        return;
      }

      if (step === 6) {
        await api.post(`/business/${bid}/onboarding`, { complete: true });

        const profile: BusinessProfile = {
          businessName: f1.business_name || '',
          businessType: f1.business_type || '',
          location: f1.location || f1.city || '',
          targetAudience: f2.target_audience || '',
          monthlyBudget: 'medium',
          primaryGoal: 'leads',
          socialPlatforms: ['Instagram', 'Facebook', 'WhatsApp', 'YouTube', 'LinkedIn'],
          teamSize: 'small',
          businessAge: 'established',
          description: f1.description || '',
        };

        let ok = true;
        try {
          const reportRes = await api.post<{ report_id: string }>('/reports/generate', profile);
          localStorage.setItem('odc_report_id', reportRes.data.report_id);
        } catch (e) {
          ok = false;
          console.error('Report generation failed:', e);
        }
        setReportGenOk(ok);
        setStep(7);
        postCompleteTimer.current = setTimeout(() => {
          if (ok) {
            nav('/onboarding-report', { replace: true });
          } else {
            nav('/dashboard', {
              replace: true,
              state: {
                onboardingReportToast:
                  'Your report is being generated — it will appear in Dashboard → Reports',
              },
            });
          }
        }, 3000);
        return;
      }
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }

  const handleNextStep = () => {
    if (busy || step === 7) return;
    void onContinue();
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div
      className="odc-page relative z-10 min-h-screen px-4 py-10"
      style={{ background: 'var(--bg)', color: 'var(--text)' }}
    >
      <style>{`
        @keyframes odc-confetti {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
        @keyframes odc-pulse-emoji {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.08); opacity: 0.92; }
        }
        .odc-onb-pulse {
          animation: odc-pulse-emoji 1.2s ease-in-out infinite;
        }
        .odc-confetti span {
          position: fixed;
          width: 8px;
          height: 12px;
          top: -20px;
          animation: odc-confetti 3s ease-in forwards;
          border-radius: 2px;
        }
      `}</style>

      <div className="mx-auto max-w-2xl">
        <div className="mb-6 h-2 overflow-hidden rounded-full" style={{ background: 'var(--card2)' }}>
          <div
            className="h-full rounded-full transition-all duration-500 ease-out"
            style={{
              width: `${(step / 7) * 100}%`,
              background: `linear-gradient(90deg, ${theme.primary}, ${theme.secondary}, ${theme.accent})`,
            }}
          />
        </div>
        <div className="mb-10 flex gap-1">
          {steps.map((_, i) => (
            <div
              key={`odc-onb-step-${i}`}
              className="h-1 flex-1 rounded-full transition"
              style={{
                background: i < step ? theme.primary : 'var(--muted)',
                opacity: i < step ? 1 : 0.35,
              }}
            />
          ))}
        </div>
        <p className="text-xs font-bold uppercase tracking-widest" style={{ color: theme.accent }}>
          Step {step} / 7 · {steps[Math.min(7, Math.max(1, step)) - 1]}
        </p>

        {error ? (
          <div
            className="mt-4 rounded-xl border px-4 py-3 text-sm"
            style={{
              borderColor: 'rgba(239,68,68,0.4)',
              background: 'rgba(239,68,68,0.12)',
              color: '#FCA5A5',
            }}
            role="alert"
          >
            {error}
          </div>
        ) : null}

        {step === 1 && (
          <div className="mt-8 space-y-4">
            <label className="block text-sm">
              <span style={{ color: 'var(--muted)' }}>Business name *</span>
              <input
                required
                className="odc-input mt-1"
                value={f1.business_name}
                onChange={(e) => {
                  setError(null);
                  setF1({ ...f1, business_name: e.target.value });
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleNextStep()}
              />
            </label>
            <label className="block text-sm">
              <span style={{ color: 'var(--muted)' }}>Business type</span>
              <select
                className="odc-select mt-1"
                value={f1.business_type}
                onChange={(e) => setF1({ ...f1, business_type: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && handleNextStep()}
              >
                <option value="">Select…</option>
                {BUSINESS_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
            <div className="grid gap-4 sm:grid-cols-2">
              <input
                className="odc-input"
                placeholder="City"
                value={f1.city}
                onChange={(e) => setF1({ ...f1, city: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && handleNextStep()}
              />
              <input
                className="odc-input"
                placeholder="State"
                value={f1.state}
                onChange={(e) => setF1({ ...f1, state: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && handleNextStep()}
              />
            </div>
            <input
              className="odc-input w-full"
              placeholder="Location / landmark"
              value={f1.location}
              onChange={(e) => setF1({ ...f1, location: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && handleNextStep()}
            />
            <textarea
              className="odc-input min-h-[6rem] w-full resize-y"
              rows={4}
              placeholder="Description"
              value={f1.description}
              onChange={(e) => setF1({ ...f1, description: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && e.ctrlKey && handleNextStep()}
            />
          </div>
        )}

        {step === 2 && (
          <div className="mt-8 space-y-4">
            <div
              className="odc-card rounded-2xl border-2 border-dashed !px-6 !py-12 text-center transition"
              style={{
                borderColor: logoDrag ? theme.primary : 'var(--border)',
                background: 'var(--card)',
                transform: 'none',
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setLogoDrag(true);
              }}
              onDragLeave={() => setLogoDrag(false)}
              onDrop={(e) => {
                e.preventDefault();
                setLogoDrag(false);
                const f = e.dataTransfer.files[0];
                if (f?.type.startsWith('image/')) onLogoFile(f);
              }}
            >
              <p style={{ color: 'var(--muted)' }}>Drop logo here or</p>
              <label className="mt-2 inline-block cursor-pointer font-bold" style={{ color: theme.primary }}>
                browse
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onLogoFile(f);
                  }}
                />
              </label>
              {f2.logo_url ? (
                <img src={f2.logo_url} alt="Logo" className="mx-auto mt-4 h-20 object-contain" />
              ) : null}
            </div>
            <label className="flex items-center gap-3 text-sm">
              <span style={{ color: 'var(--muted)' }}>Primary color</span>
              <input
                type="color"
                value={f2.primary_color}
                onChange={(e) => setF2({ ...f2, primary_color: e.target.value })}
              />
            </label>
            <input className="odc-input w-full" placeholder="Tagline" value={f2.tagline} onChange={(e) => setF2({ ...f2, tagline: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && handleNextStep()} />
            <textarea
              className="odc-input min-h-[5rem] w-full resize-y"
              rows={3}
              placeholder="Target audience description"
              value={f2.target_audience}
              onChange={(e) => setF2({ ...f2, target_audience: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && e.ctrlKey && handleNextStep()}
            />
          </div>
        )}

        {step === 3 && (
          <div className="mt-8 space-y-4">
            {[
              ['Facebook', 'Connect Facebook Page'],
              ['Instagram', 'Connect Instagram'],
              ['YouTube', 'Connect YouTube'],
              ['LinkedIn', 'Connect LinkedIn'],
            ].map(([name, label]) => (
              <div
                key={name}
                className="odc-card flex flex-wrap items-center justify-between gap-3 !rounded-xl !p-4"
                style={{ transform: 'none' }}
              >
                <span className="font-semibold">{name}</span>
                <div className="flex gap-2">
                  <button type="button" className="odc-btn-primary !text-xs" onClick={() => alert(`${label} (demo OAuth)`)}>
                    {label}
                  </button>
                  <button type="button" className="odc-btn-ghost !text-xs">
                    Skip
                  </button>
                </div>
              </div>
            ))}
            <div className="odc-card !rounded-xl !p-4" style={{ transform: 'none' }}>
              <p className="font-semibold">WhatsApp Business</p>
              <input
                className="odc-input mt-2 !py-2.5 text-sm"
                placeholder="Enter WhatsApp Business number"
                value={waNumber}
                onChange={(e) => setWaNumber(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleNextStep()}
              />
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="odc-card mt-8 !rounded-2xl !p-6" style={{ transform: 'none' }}>
            <h3 className="text-lg font-bold" style={{ color: 'var(--text)' }}>
              ElevenLabs voice clone
            </h3>
            <p className="mt-2 text-sm" style={{ color: 'var(--muted)' }}>
              We need <strong>3 short recordings</strong> (~30s each) in a quiet room for best results.
            </p>
            <button type="button" className="odc-btn-primary mt-6 w-full justify-center !py-3.5" onClick={() => alert('Recording UI (demo)')}>
              Record voice sample
            </button>
            <button type="button" className="odc-btn-ghost mt-3 w-full justify-center !py-3 text-sm" onClick={() => {}}>
              Skip for now
            </button>
          </div>
        )}

        {step === 5 && (
          <div className="mt-8">
            <p className="text-center text-lg font-bold" style={{ color: 'var(--text)' }}>
              AI has generated your first 90 days of content!
            </p>
            <p className="mt-2 text-center text-sm" style={{ color: 'var(--muted)' }}>
              Preview your first week below — approve to activate.
            </p>
            <div className="mt-6 grid grid-cols-7 gap-2">
              {dummyWeek.map((d) => (
                <div
                  key={d.day}
                  className="odc-card rounded-lg !p-2 text-center text-[10px]"
                  style={{ transform: 'none' }}
                >
                  <div className="font-bold" style={{ color: theme.accent }}>
                    {d.day}
                  </div>
                  <div className="mt-2" style={{ color: 'var(--muted)' }}>
                    {d.title}
                  </div>
                </div>
              ))}
            </div>
            <button type="button" className="odc-btn-primary mt-8 w-full justify-center !py-3.5" style={{ background: theme.success }}>
              Approve calendar
            </button>
          </div>
        )}

        {step === 7 && (
          <div className="relative mt-10 text-center">
            <div className="odc-onb-pulse text-7xl leading-none">✨</div>
            <h2 className="mt-6 font-['Space_Grotesk',sans-serif] text-3xl font-black" style={{ color: 'var(--text)' }}>
              Your ODC setup is complete!
            </h2>
            <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed" style={{ color: 'var(--muted)' }}>
              ODC AI is preparing a personalized report for your business...
            </p>
            {!reportGenOk ? (
              <p className="mx-auto mt-3 max-w-md text-xs" style={{ color: '#FBBF24' }}>
                The report service is currently unavailable — you will be redirected to the dashboard.
              </p>
            ) : null}
            <ul className="mx-auto mt-10 max-w-md space-y-3 text-left text-sm" style={{ color: 'var(--muted)' }}>
              <li className="flex gap-2">
                <span style={{ color: theme.success }}>✅</span>
                Business profile saved
              </li>
              <li className="flex gap-2">
                <span style={{ color: theme.success }}>✅</span>
                AI agents analyzed
              </li>
              <li className="flex gap-2">
                <span style={{ color: theme.success }}>✅</span>
                Generating your content plan...
              </li>
              <li className="flex gap-2">
                <span>⏳</span>
                Preparing your agent recommendations...
              </li>
            </ul>
          </div>
        )}

        {step === 6 && (
          <div className="relative mt-8 text-center">
            <div className="odc-confetti pointer-events-none">
              {Array.from({ length: 24 }).map((_, i) => (
                <span
                  key={i}
                  style={{
                    left: `${(i * 37) % 100}%`,
                    background: ['#10B981', '#34D399', '#06B6D4', '#F59E0B'][i % 4],
                    animationDelay: `${(i % 8) * 0.15}s`,
                  }}
                />
              ))}
            </div>
            <div className="text-6xl">🚀</div>
            <h2 className="mt-4 font-['Space_Grotesk',sans-serif] text-3xl font-black" style={{ color: 'var(--text)' }}>
              GO LIVE!
            </h2>
            <p className="mt-2" style={{ color: 'var(--muted)' }}>
              Your AI agents are now active!
            </p>
            <ul className="mx-auto mt-8 max-w-sm space-y-3 text-left text-sm" style={{ color: 'var(--muted)' }}>
              {[
                'Digital twin created',
                'Content calendar ready (90 days)',
                'Lead scoring active',
                'Social accounts monitored',
              ].map((t, i) => (
                <li
                  key={t}
                  className="odc-checklist-item flex gap-2"
                  style={{ animationDelay: `${0.15 + i * 0.12}s` }}
                >
                  <span style={{ color: theme.success }}>✅</span>
                  {t}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-10 flex gap-4">
          {step > 1 && step < 6 ? (
            <button type="button" className="odc-btn-ghost px-6 py-3 font-semibold" onClick={() => setStep((s) => Math.max(1, s - 1))}>
              Back
            </button>
          ) : null}
          <button
            type="button"
            disabled={busy || step === 7}
            className="odc-btn-primary ml-auto !px-8 !py-3 disabled:opacity-50"
            onClick={handleNextStep}
          >
            {step === 7
              ? 'Please wait…'
              : busy
                ? 'Please wait…'
                : step === 6
                  ? 'Complete Onboarding'
                  : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
